import {
  Component,
  Injector,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import {
  NgDiagramBackgroundComponent,
  NgDiagramComponent,
  NgDiagramModelService,
  NgDiagramNodeTemplateMap,
  NgDiagramService,
  NgDiagramViewportService,
  initializeModel,
  provideNgDiagram,
  type NgDiagramConfig,
  type Point,
  type SelectionMovedEvent,
} from 'ng-diagram';
import { TranslocoPipe } from '@jsverse/transloco';
import type { OrgTreeNodeDto } from '../../../shared';
import { OrgUnitNodeComponent } from './org-unit-node.component';
import {
  type DiagramData,
  orgTreeToDiagram,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_TO_FIT_PADDING,
} from './org-chart.model';
import { OrgChartZoomControlsComponent } from './org-chart-zoom-controls.component';
import {
  OrgChartExpandControlsComponent,
  ORG_CHART_TOOLBAR_BUTTON_CLASS,
} from './org-chart-expand-controls.component';
import { OrgChartStore, orgChartDropListId } from './org-chart.state';

/**
 * Organigram diagram: renders Organisation › OE, each OE card listing its
 * positions, each position its assignees. Persons are dragged (CDK) between
 * position rows to reassign; a row's edit icon requests an edit when permitted.
 */
@Component({
  selector: 'org-chart',
  standalone: true,
  imports: [
    NgDiagramComponent,
    NgDiagramBackgroundComponent,
    OrgChartZoomControlsComponent,
    OrgChartExpandControlsComponent,
    TranslocoPipe,
  ],
  providers: [provideNgDiagram()],
  styles: [
    `
      :host {
        display: flex;
        position: relative;
        min-height: 0;
        flex: 1 1 auto;
        width: 100%;
      }
      ng-diagram {
        display: flex;
        flex: 1 1 auto;
        min-height: 0;
        width: 100%;
      }
    `,
  ],
  template: `
    @if (model(); as m) {
      <ng-diagram
        [model]="m"
        [nodeTemplateMap]="nodeTemplateMap"
        [config]="diagramConfig"
        (selectionMoved)="onSelectionMoved($event)"
      >
        <ng-diagram-background />
      </ng-diagram>
      <div class="absolute bottom-4 left-4 z-10 flex items-center gap-2">
        <org-chart-zoom-controls />
        <org-chart-expand-controls />
        <div
          class="flex items-center rounded-lg border border-border bg-card p-1 shadow-sm"
        >
          <button
            type="button"
            [class]="toolbarButtonClass"
            [title]="'organigram.toolbar.arrangeHint' | transloco"
            (click)="arrange()"
          >
            <span class="material-icons text-base" aria-hidden="true">lan</span>
            {{ 'organigram.toolbar.arrange' | transloco }}
          </button>
        </div>
      </div>
    }
  `,
})
export class OrgChartComponent {
  readonly tree = input.required<OrgTreeNodeDto | null>();

  readonly #injector = inject(Injector);
  readonly #store = inject(OrgChartStore);
  readonly #diagramService = inject(NgDiagramService);
  readonly #modelService = inject(NgDiagramModelService);
  readonly #viewportService = inject(NgDiagramViewportService);

  readonly nodeTemplateMap = new NgDiagramNodeTemplateMap([
    ['orgUnit', OrgUnitNodeComponent],
  ]);

  /**
   * Node dragging owns card surfaces; person rows and interactive controls use
   * data-no-drag so their CDK/click gestures remain isolated from ng-diagram.
   * `zoomToFit` centres the whole tree in the viewport on load. Edges are
   * orthogonal with rounded corners, on the routes `busRoute` computes.
   */
  readonly diagramConfig = {
    nodeDraggingEnabled: true,
    edgeRouting: {
      defaultRouting: 'orthogonal',
      orthogonal: { maxCornerRadius: 8 },
    },
    zoom: {
      min: ZOOM_MIN,
      max: ZOOM_MAX,
      zoomToFit: { onInit: true },
    },
  } satisfies NgDiagramConfig;

  readonly #diagram = computed(() => {
    const tree = this.tree();
    return tree ? orgTreeToDiagram(tree, this.#store.expansion()) : null;
  });
  /** The diagram together with the tree it was built from. */
  readonly #laidOutDiagram = computed<{
    tree: OrgTreeNodeDto;
    diagram: DiagramData;
  } | null>(() => {
    const tree = this.tree();
    const diagram = this.#diagram();
    return tree && diagram ? { tree, diagram } : null;
  });
  readonly #manualNodePositions = new Map<string, Point>();
  #lastFittedTree: OrgTreeNodeDto | null = null;
  readonly toolbarButtonClass = ORG_CHART_TOOLBAR_BUTTON_CLASS;

  /**
   * The model is only rebuilt when the tree changes. Expand/collapse toggles
   * re-run `#diagram` and are applied in-place via `updateNodes` (see the
   * effect below), so the viewport does not reset on every toggle.
   */
  readonly model = computed(() => {
    const tree = this.tree();
    if (!tree) return null;
    const diagram = untracked(() => this.#diagram());
    if (!diagram) return null;
    return initializeModel(
      {
        nodes: diagram.nodes.map((node) => ({
          ...node,
          position: this.#manualNodePositions.get(node.id) ?? node.position,
        })),
        edges: diagram.edges,
      },
      this.#injector,
    );
  });

  /**
   * Keep user-positioned nodes where they were dropped. The bus routes are
   * manual, so connected edges switch to ng-diagram's live auto routing on
   * first move; unaffected edges keep their bus.
   */
  onSelectionMoved({ nodes }: SelectionMovedEvent): void {
    const movedNodeIds = new Set(nodes.map((node) => node.id));
    for (const node of nodes) {
      this.#manualNodePositions.set(node.id, { ...node.position });
    }

    const edgesToUpdate = this.#modelService
      .edges()
      .filter(
        (edge) =>
          edge.routingMode === 'manual' &&
          (movedNodeIds.has(edge.source) || movedNodeIds.has(edge.target)),
      )
      .map(({ id }) => ({
        id,
        points: undefined,
        routing: 'orthogonal',
        routingMode: 'auto' as const,
      }));

    if (edgesToUpdate.length > 0) {
      this.#modelService.updateEdges(edgesToUpdate);
    }
  }

  constructor() {
    // Connect only manageable position lists, so CDK drag-drop links the rows a
    // user may actually move persons between (the server also enforces both ends).
    effect(() => {
      const diagram = this.#diagram();
      const ids =
        diagram?.nodes.flatMap((n) =>
          n.data.positions
            .filter((p) => p.canManage)
            .map((p) => orgChartDropListId(p.positionId)),
        ) ?? [];
      this.#store.setDropListIds(ids);
    });

    // Apply the layout in-place. This preserves the viewport when a position
    // expands; a genuinely new tree is fitted once its layout is applied.
    effect(() => {
      const result = this.#laidOutDiagram();
      const initialized = this.#diagramService.isInitialized();
      if (!result) {
        this.#manualNodePositions.clear();
        return;
      }
      if (!initialized) return;

      untracked(() => {
        const fit = result.tree !== this.#lastFittedTree;
        this.#lastFittedTree = result.tree;
        this.#apply(result.diagram, fit);
      });
    });
  }

  /**
   * Lay the chart out again as it was loaded: what was dragged goes back to
   * its place, edges go back onto the bus, and the whole tree is fitted.
   *
   * Dragged cards are only remembered for the session, so a reload did the
   * same — this spares the reload. They are kept otherwise because someone
   * moved them on purpose; but after a unit is added or removed, the rest is
   * laid out anew around them and they no longer fit in.
   */
  arrange(): void {
    const result = this.#laidOutDiagram();
    if (!result || !this.#diagramService.isInitialized()) return;
    this.#manualNodePositions.clear();
    this.#apply(result.diagram, true);
  }

  /** Place the nodes and route the edges; dragged nodes keep their spot. */
  #apply(diagram: DiagramData, fit: boolean): void {
    const currentNodeIds = new Set(diagram.nodes.map((node) => node.id));
    for (const nodeId of this.#manualNodePositions.keys()) {
      if (!currentNodeIds.has(nodeId)) {
        this.#manualNodePositions.delete(nodeId);
      }
    }

    this.#modelService.updateNodes(
      diagram.nodes.map(({ id, position, size }) => ({
        id,
        position: this.#manualNodePositions.get(id) ?? position,
        size,
      })),
    );
    this.#modelService.updateEdges(
      diagram.edges.map(
        ({ id, source, target, points, routing, routingMode }) =>
          this.#manualNodePositions.has(source) ||
          this.#manualNodePositions.has(target)
            ? {
                id,
                points: undefined,
                routing: 'orthogonal',
                routingMode: 'auto' as const,
              }
            : {
                id,
                points,
                routing,
                routingMode,
              },
      ),
    );

    if (fit) {
      this.#viewportService.zoomToFit({ padding: ZOOM_TO_FIT_PADDING });
    }
  }
}
